import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies, { CookieAttributes } from 'js-cookie';
import { useLocation } from 'react-router-dom';

import { getMobileNativeWebView } from 'src/common/utils/reduxStoreSelectors';
import { COOKIE_POLICY_DATE, EXTERNAL_PATHS } from '../../constants';
import { open, close } from '../../ducks/zippy';
import CookieContent from '../CookieContent';
import ComplianceUpdates from '../ComplianceUpdates';
import { ReduxState } from '../../../types/reduxState';

const cookieName = `${COOKIE_POLICY_DATE}-cookie-policy-acknowledged`;
const cookieAttributes: CookieAttributes = {
  expires: 365 * 10,
  path: '/',
  sameSite: 'Lax',
};

// Useberry overwrites references of itself in all source code, so this prevents it for this const
// eslint-disable-next-line no-useless-concat
const USEBERRY_IFRAME_HOST = 'https://a' + 'pp.us' + 'eberry.com';

const complianceModalBlacklist = [
  EXTERNAL_PATHS.TERMS,
  EXTERNAL_PATHS.COOKIES_POLICY,
  EXTERNAL_PATHS.PRIVACY_POLICY,
  EXTERNAL_PATHS.MEMBER_INTERFACE,
];

const GdprCompliance: FC = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const loggedIn = useSelector<ReduxState, boolean | null>(
    ({ user }) => user.get('loggedIn') ?? null,
  );

  const termsAgreed = useSelector<ReduxState, boolean | undefined>(
    ({ user }) => user.get('terms_agreed', false),
  );

  const isNativeWebView = useSelector(getMobileNativeWebView);

  const cookieBannerOpened = useSelector<ReduxState, boolean>(
    ({ zippies }) => zippies.get(cookieName, false),
  );

  const renderModal = loggedIn && !termsAgreed && !complianceModalBlacklist.includes(pathname);

  const renderCookieBanner = !isNativeWebView && ((loggedIn !== null && !loggedIn) || !termsAgreed);

  const closePopup = useCallback(() => {
    dispatch(close(cookieName));

    const attributes = { ...cookieAttributes };

    // Support cross-site cookies for Useberry, so that users don't
    // get spammed by the cookie banner while testing the site
    if (window.location.ancestorOrigins?.contains(USEBERRY_IFRAME_HOST)) {
      attributes.sameSite = 'None';
      // Required or the cookie won't be set
      attributes.secure = true;
    }

    Cookies.set(cookieName, 'true', attributes);
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn !== null && !loggedIn && !cookieBannerOpened) {
      const cookieIsSet = Cookies.get(cookieName);
      if (!cookieIsSet) {
        dispatch(open(cookieName));
      }
    }
  }, [loggedIn, cookieBannerOpened, dispatch]);

  return (
    <>
      {renderModal && <ComplianceUpdates />}
      {cookieBannerOpened && renderCookieBanner && (
        <CookieContent handleClose={closePopup} />
      )}
    </>
  );
};

export default GdprCompliance;
